import { useCallback, useEffect, useRef, useState } from "react";

/** Below this distance from the bottom the reader counts as "at the bottom" and follow resumes. */
const AT_BOTTOM_PX = 80;
/** Above this distance the jump-to-latest affordance appears. */
const SHOW_JUMP_PX = 120;

export interface ChatAutoscroll {
	/** Attach to the scrolling element. */
	scrollRef: React.RefObject<HTMLDivElement>;
	/** True when the reader has scrolled away from the bottom. */
	showJump: boolean;
	/** Scroll to the newest content and re-enable follow mode. */
	jumpToLatest: () => void;
}

/**
 * Follow-the-conversation scrolling.
 *
 * The chat previously ran `scrollTo({ behavior: "smooth" })` on every message/streaming change with
 * no conditions, so scrolling up to re-read an earlier plan yanked the viewport back to the bottom
 * on the next streamed token — the reader physically could not stay put during a run.
 *
 * This tracks explicit intent instead of guessing from distance: a wheel/touch/keyboard scroll UP
 * suspends follow immediately, and it only resumes when the reader returns to the bottom (or presses
 * the jump affordance). Distance alone is not enough — content growing beneath the viewport moves the
 * reader away from the bottom without them having scrolled, which would silently disable follow.
 *
 * `deps` are the values whose change means "new content arrived" (message count, streaming flag, …).
 */
export function useChatAutoscroll(deps: ReadonlyArray<unknown>): ChatAutoscroll {
	const scrollRef = useRef<HTMLDivElement>(null);
	const userScrolledUpRef = useRef(false);
	const frameRef = useRef(0);
	const [showJump, setShowJump] = useState(false);

	const scrollToBottom = useCallback((smooth = false) => {
		cancelAnimationFrame(frameRef.current);
		frameRef.current = requestAnimationFrame(() => {
			const el = scrollRef.current;
			if (!el) return;
			if (smooth) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
			else el.scrollTop = el.scrollHeight;
		});
	}, []);

	const jumpToLatest = useCallback(() => {
		userScrolledUpRef.current = false;
		setShowJump(false);
		scrollToBottom(true);
	}, [scrollToBottom]);

	// Track position + intent.
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const onScroll = () => {
			const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			if (distanceFromBottom <= AT_BOTTOM_PX) userScrolledUpRef.current = false;
			setShowJump(distanceFromBottom > SHOW_JUMP_PX);
		};
		const suspendOnUpward = (deltaY: number) => {
			if (deltaY < 0) userScrolledUpRef.current = true;
		};
		const onWheel = (e: WheelEvent) => suspendOnUpward(e.deltaY);
		// Touch: remember the last Y and derive direction, so the same intent rule works on tablets.
		let lastTouchY = 0;
		const onTouchStart = (e: TouchEvent) => {
			lastTouchY = e.touches[0]?.clientY ?? 0;
		};
		const onTouchMove = (e: TouchEvent) => {
			const y = e.touches[0]?.clientY ?? 0;
			// Dragging DOWN reveals earlier content, i.e. scrolls up.
			suspendOnUpward(lastTouchY - y);
			lastTouchY = y;
		};

		el.addEventListener("scroll", onScroll, { passive: true });
		el.addEventListener("wheel", onWheel, { passive: true });
		el.addEventListener("touchstart", onTouchStart, { passive: true });
		el.addEventListener("touchmove", onTouchMove, { passive: true });
		return () => {
			el.removeEventListener("scroll", onScroll);
			el.removeEventListener("wheel", onWheel);
			el.removeEventListener("touchstart", onTouchStart);
			el.removeEventListener("touchmove", onTouchMove);
		};
	}, []);

	// Follow new content, unless the reader has deliberately scrolled up.
	useEffect(() => {
		if (userScrolledUpRef.current) return;
		scrollToBottom();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

	return { scrollRef, showJump, jumpToLatest };
}
